import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Button, Typography, IconButton, Switch, FormControlLabel } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { changeAddModalView, setCreateDates, submitPantryItemCreate } from "../../../../../actions/pantry";
import { Close as CloseIcon } from "@mui/icons-material";

const ExpiryDate = ({ pantry, changeAddModalView, setCreateDates, submitPantryItemCreate }) => {
  const [notifyDate, setNotifyDate] = useState(pantry.create?.data?.dates?.notifyDate || null);
  const [expiryDate, setExpiryDate] = useState(pantry.create?.data?.dates?.expiryDate || null);
  const [opened, setOpened] = useState(pantry.create?.data?.dates?.opened || false);
  const [openedDate, setOpenedDate] = useState(pantry.create?.data?.dates?.openedDate || null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [expiryOpen, setExpiryOpen] = useState(false);
  const [openedOpen, setOpenedOpen] = useState(false);
  const notifyInputRef = useRef();
  const expiryInputRef = useRef();
  const openedInputRef = useRef();

  const handleNext = () => {
    setCreateDates({ notifyDate, expiryDate, opened, openedDate });
    submitPantryItemCreate();
    // Optionally: close modal or show success
  };

  const handleBack = () => {
    changeAddModalView({ stage: "ingredients" });
  };

  // Custom onClick handlers for each date field
  const handleNotifyClick = (e) => {
    if (!notifyOpen) {
      setNotifyOpen(true);
      e.preventDefault();
      e.stopPropagation();
    } else if (notifyInputRef.current) {
      notifyInputRef.current.select();
    }
  };
  const handleExpiryClick = (e) => {
    if (!expiryOpen) {
      setExpiryOpen(true);
      e.preventDefault();
      e.stopPropagation();
    } else if (expiryInputRef.current) {
      expiryInputRef.current.select();
    }
  };
  const handleOpenedClick = (e) => {
    if (!openedOpen) {
      setOpenedOpen(true);
      e.preventDefault();
      e.stopPropagation();
    } else if (openedInputRef.current) {
      openedInputRef.current.select();
    }
  };

  const clearNotify = () => setNotifyDate(null);
  const clearExpiry = () => setExpiryDate(null);
  const clearOpened = () => setOpenedDate(null);

  return (
    <div className="w-full">
      <div className="p-4 bg-neutral-100">
        <Typography variant="h6" className="font-normal">
          Ingredient Dates
        </Typography>
      </div>
      <div className="p-4 flex flex-col gap-8">
        <div>
          <div className="flex items-center justify-between mb-2">
            <Typography variant="subtitle2" className="font-normal">
              Notify Date
            </Typography>
            <div className="flex items-center gap-1">
              {notifyDate && (
                <IconButton size="small" onClick={clearNotify}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </div>
          </div>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Notify Date"
              value={notifyDate}
              onChange={setNotifyDate}
              format="yyyy-MM-dd"
              open={notifyOpen}
              onOpen={() => setNotifyOpen(true)}
              onClose={() => setNotifyOpen(false)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: "outlined",
                  inputRef: notifyInputRef,
                  onClick: handleNotifyClick,
                },
              }}
              clearable
            />
          </LocalizationProvider>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <Typography variant="subtitle2" className="font-normal">
              Best Before
            </Typography>
            <div className="flex items-center gap-1">
              {expiryDate && (
                <IconButton size="small" onClick={clearExpiry}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </div>
          </div>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Best Before"
              value={expiryDate}
              onChange={setExpiryDate}
              format="yyyy-MM-dd"
              open={expiryOpen}
              onOpen={() => setExpiryOpen(true)}
              onClose={() => setExpiryOpen(false)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: "outlined",
                  inputRef: expiryInputRef,
                  onClick: handleExpiryClick,
                },
              }}
              clearable
            />
          </LocalizationProvider>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <FormControlLabel
              control={<Switch checked={opened} onChange={(_, checked) => setOpened(checked)} />}
              label={
                <Typography variant="subtitle2" className="font-normal">
                  Opened
                </Typography>
              }
            />
            {opened && openedDate && (
              <IconButton size="small" onClick={clearOpened}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </div>
          {opened && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Opened Date"
                value={openedDate}
                onChange={setOpenedDate}
                format="yyyy-MM-dd"
                open={openedOpen}
                onOpen={() => setOpenedOpen(true)}
                onClose={() => setOpenedOpen(false)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    inputRef: openedInputRef,
                    onClick: handleOpenedClick,
                  },
                }}
                clearable
              />
            </LocalizationProvider>
          )}
        </div>
        <div className="flex justify-between mt-6 gap-2">
          <Button
            variant="contained"
            color="secondary"
            className="text-primaryText"
            onClick={handleBack}
            disableRipple
            disableElevation
          >
            Back
          </Button>
          <Button variant="contained" color="primary" onClick={handleNext} disableRipple disableElevation>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

ExpiryDate.propTypes = {
  pantry: PropTypes.object.isRequired,
  changeAddModalView: PropTypes.func.isRequired,
  setCreateDates: PropTypes.func.isRequired,
  submitPantryItemCreate: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  pantry: state.pantry,
});

export default connect(mapStateToProps, { changeAddModalView, setCreateDates, submitPantryItemCreate })(ExpiryDate);
